import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { fmtCurrency, fmtDate } from '../lib/localeUtils';
import {
  AlertTriangle, Check, Loader2, RefreshCw, ShieldCheck, Lock,
  PackageMinus, CheckCircle2, Layers,
} from 'lucide-react';

const STRATEGIES = {
  accept: {
    label: 'Accept as-is',
    hint: 'Nothing was oversold — stock already reflects what was on hand. Marks the sale reviewed.',
    icon: CheckCircle2,
  },
  writeoff: {
    label: 'Write off the shortfall',
    hint: 'Records a stock adjustment for the units handed over but never deducted, so the books reconcile.',
    icon: PackageMinus,
  },
};

const FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all', label: 'All' },
];

const shortfallOf = (item) => Math.max(0, (item.requested_qty || 0) - (item.available_qty || 0));

export default function SyncConflictsPage() {
  const { user } = useAuth();
  const canManage = ['super_admin', 'admin', 'inventory_manager'].includes(user?.role)
    || (Array.isArray(user?.custom_permissions) && user.custom_permissions.includes('inventory_manage'));

  const [filter, setFilter] = useState('open');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [strategy, setStrategy] = useState('accept');
  const [note, setNote] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sync/conflicts', { params: { status: filter } });
      setRows(data.conflicts || []);
      setCounts({ open: data.open_count || 0, resolved: data.resolved_count || 0 });
    } catch {
      toast.error('Could not load offline sync conflicts');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const resolveOne = async (saleId, chosen) => {
    setBusyId(saleId);
    try {
      const { data } = await api.post(`/sync/conflicts/${saleId}/resolve`, {
        strategy: chosen, note: note.trim() || undefined,
      });
      const adjusted = data.adjustments?.length || 0;
      toast.success(
        chosen === 'writeoff'
          ? `Resolved — ${adjusted} stock adjustment(s) recorded`
          : 'Marked as reviewed',
      );
      // Patch locally so a long list does not re-fetch on every decision.
      setCounts((c) => ({ open: Math.max(0, c.open - 1), resolved: c.resolved + 1 }));
      if (filter === 'open') {
        setRows((prev) => prev.filter((r) => r.sale_id !== saleId));
      } else {
        setRows((prev) => prev.map((r) => r.sale_id === saleId ? {
          ...r,
          conflict_resolved: true,
          conflict_resolution: chosen,
          conflict_resolution_note: note.trim() || null,
          conflict_resolved_at: new Date().toISOString(),
          resolved_by_name: user?.name || '',
        } : r));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resolve this conflict');
    } finally {
      setBusyId(null);
    }
  };

  const resolveAll = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post('/sync/conflicts/resolve-all', {
        strategy, note: note.trim() || undefined,
      });
      toast.success(`Resolved ${data.resolved} conflict(s)${data.adjustments?.length ? ` · ${data.adjustments.length} stock adjustment(s)` : ''}`);
      if (data.failed) {
        toast.error(`${data.failed} conflict(s) could not be resolved — try again or resolve them individually`);
      }
      setBulkOpen(false);
      setNote('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk resolve failed');
    } finally {
      setBulkBusy(false);
    }
  };

  if (!canManage) {
    return (
      <div className="max-w-xl mx-auto py-24 text-center" data-testid="sync-conflicts-denied">
        <Lock className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Manager access required</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Offline sync review is limited to admins and inventory managers.
        </p>
      </div>
    );
  }

  const StrategyIcon = STRATEGIES[strategy].icon;

  return (
    <div className="space-y-6" data-testid="sync-conflicts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Offline Sync Review
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Sales that synced after a connectivity drop where an item had less stock on the books
            than the cashier saw offline. Nothing was oversold — decide how each one should settle.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={load} disabled={loading} data-testid="refresh-conflicts-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setBulkOpen(true)}
            disabled={counts.open === 0}
            data-testid="bulk-resolve-btn"
          >
            <Layers className="h-3.5 w-3.5" /> Resolve all open ({counts.open})
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-full border bg-muted/40 p-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filter === f.id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`conflicts-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Badge variant="outline" className="gap-1.5 border-amber-400 text-amber-700" data-testid="open-conflicts-count">
          <AlertTriangle className="h-3 w-3" /> {counts.open} open
        </Badge>
        <Badge variant="outline" className="gap-1.5 border-emerald-400 text-emerald-700" data-testid="resolved-conflicts-count">
          <ShieldCheck className="h-3 w-3" /> {counts.resolved} resolved
        </Badge>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Resolution strategy</Label>
        <div className="flex items-start gap-3 flex-wrap">
          <Select value={strategy} onValueChange={setStrategy}>
            <SelectTrigger className="w-64" data-testid="strategy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STRATEGIES).map(([key, s]) => (
                <SelectItem key={key} value={key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground max-w-md flex items-center gap-1.5 pt-2" data-testid="strategy-hint">
            <StrategyIcon className="h-3.5 w-3.5 shrink-0" /> {STRATEGIES[strategy].hint}
          </p>
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note saved on the sale (and on any stock adjustment)"
          className="h-16 text-sm resize-none max-w-2xl"
          data-testid="resolution-note-input"
        />
      </div>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground" data-testid="conflicts-loading">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-muted-foreground" data-testid="no-conflicts-state">
            <ShieldCheck className="h-8 w-8 mx-auto mb-3 text-emerald-500 opacity-70" />
            <p className="text-sm font-medium">Nothing to review</p>
            <p className="text-xs mt-1">Offline sales are syncing cleanly.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <Card
              key={row.sale_id}
              className={`border-l-4 ${row.conflict_resolved ? 'border-l-emerald-500' : 'border-l-amber-500'}`}
              data-testid={`conflict-row-${row.sale_id}`}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">
                      Sale {row.sale_id}
                      <span className="ml-2 font-normal text-muted-foreground">{fmtCurrency(row.total || 0)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {row.customer_name || 'Walk-in'}
                      {row.customer_phone ? ` · ${row.customer_phone}` : ''}
                      {row.cashier_name ? ` · cashier ${row.cashier_name}` : ''}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Sold {fmtDate(row.created_at)} · synced {fmtDate(row.synced_at)}
                    </p>
                  </div>
                  {row.conflict_resolved ? (
                    <Badge variant="outline" className="border-emerald-400 text-emerald-700 gap-1" data-testid={`conflict-resolved-badge-${row.sale_id}`}>
                      <Check className="h-3 w-3" />
                      {STRATEGIES[row.conflict_resolution]?.label || 'Reviewed'}
                    </Badge>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === row.sale_id}
                        onClick={() => resolveOne(row.sale_id, 'accept')}
                        data-testid={`accept-conflict-${row.sale_id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-400 text-amber-700 hover:bg-amber-50"
                        disabled={busyId === row.sale_id}
                        onClick={() => resolveOne(row.sale_id, 'writeoff')}
                        data-testid={`writeoff-conflict-${row.sale_id}`}
                      >
                        {busyId === row.sale_id
                          ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          : <PackageMinus className="h-3.5 w-3.5 mr-1" />}
                        Write off
                      </Button>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border bg-muted/30 divide-y">
                  {(row.conflicts || []).map((item, i) => (
                    <div
                      key={item.product_id || `${row.sale_id}-${i}`}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                      data-testid={`conflict-item-${row.sale_id}-${item.product_id || i}`}
                    >
                      <span className="font-medium truncate">{item.product_name}</span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        sold {item.requested_qty} · on books {item.available_qty} ·{' '}
                        <span className="text-amber-700 font-semibold">short {shortfallOf(item)}</span>
                      </span>
                    </div>
                  ))}
                </div>

                {row.conflict_resolved && (
                  <p className="text-[11px] text-muted-foreground" data-testid={`conflict-resolution-meta-${row.sale_id}`}>
                    Resolved {fmtDate(row.conflict_resolved_at)}
                    {row.resolved_by_name ? ` by ${row.resolved_by_name}` : ''}
                    {row.conflict_resolution_note ? ` — “${row.conflict_resolution_note}”` : ''}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-resolve-dialog">
          <DialogHeader>
            <DialogTitle>Resolve all {counts.open} open conflict(s)?</DialogTitle>
            <DialogDescription className="text-xs">
              Applying <strong>{STRATEGIES[strategy].label}</strong> to every open conflict in this branch.
              {' '}{STRATEGIES[strategy].hint}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel-btn">Cancel</Button>
            <Button onClick={resolveAll} disabled={bulkBusy} data-testid="bulk-confirm-btn">
              {bulkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Apply to all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
